import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ContactCard } from "@/components/ui/contact/contactCard";

type EcPageProps = {
  onNavigate?: (path: string) => void;
};

const features = [
  {
    title: "現地語対応",
    description:
      "ネイティブ品質の翻訳で、進出先の言語に合わせたストア表示を自動で実現します。",
  },
  {
    title: "GEO/SEO（AI検索・検索エンジン最適化）",
    description:
      "国・地域ごとの検索エンジンに最適化し、海外の見込み客に見つけてもらえる状態をつくります。",
  },
  {
    title: "スムーズな導入",
    description:
      "Shopifyに追加するだけで設定が完結し、専門知識がなくてもすぐに使い始められます。",
  },
  {
    title: "継続的なアップデート",
    description:
      "利用データをもとに翻訳精度と表示を継続的に改善し、成果を伸ばし続けます。",
  },
] as const;

export function EcPage({ onNavigate }: EcPageProps) {
  useEffect(() => {
    const prev = document.title;
    document.title =
      "Autranslate（オートラ）| Shopify向け SEO/GEO対応翻訳AI | Monobundle Inc.";
    return () => {
      document.title = prev;
    };
  }, []);

  const go = (path: string) => {
    if (onNavigate) onNavigate(path);
    else if (typeof window !== "undefined") {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  return (
    <>
      <section className="border-b border-border/60 px-6 py-16 md:px-12">
        <div className="mx-auto max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Cross-border EC
          </p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight text-foreground md:text-5xl">
            Autranslate
          </h1>
          <p className="mt-2 text-lg text-muted-foreground">オートラ | SEO/GEO対応翻訳AI</p>
          <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">
            ストアの海外対応を、インストールするだけで実現する Shopify アプリです。商品コンテンツの AI
            翻訳、レビュー、公開までを一つのワークフローで支援し、越境 EC の立ち上げと成長を加速します。
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button variant="outline" size="sm" asChild>
              <a href="/service/ec/terms" onClick={(e) => { e.preventDefault(); go("/service/ec/terms"); }}>
                Terms of Service
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a
                href="/service/ec/privacypolicy"
                onClick={(e) => {
                  e.preventDefault();
                  go("/service/ec/privacypolicy");
                }}
              >
                Privacy Policy
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-serif text-2xl font-light tracking-tight text-foreground md:text-3xl">
            できること
          </h2>
          <ul className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border/60 bg-border/60 sm:grid-cols-2">
            {features.map((item) => (
              <li key={item.title} className="group">
                <Card className="h-full rounded-none border-0 bg-card shadow-none transition-all duration-300 hover:bg-muted/15">
                  <CardHeader className="pb-3">
                    <h3 className="text-base font-bold tracking-tight text-foreground">{item.title}</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-border/60 bg-muted/20 px-6 py-20 md:px-12">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-serif text-2xl font-light tracking-tight text-foreground md:text-3xl">
            料金（概要）
          </h2>
          <p className="mt-6 text-sm text-muted-foreground leading-relaxed md:text-base">
            Shopify Billing 経由の月額サブスクリプション（USD $5.00 / 月）と、月間 10,000
            翻訳クレジットを含む従量課金モデルです。詳細は{" "}
            <a
              href="/service/ec/terms"
              className="text-foreground underline decoration-border underline-offset-4"
              onClick={(e) => {
                e.preventDefault();
                go("/service/ec/terms");
              }}
            >
              利用規約
            </a>
            をご確認ください。
          </p>
        </div>
      </section>

      <section className="px-6 py-20 md:px-12">
        <div className="mx-auto max-w-4xl">
          <ContactCard />
        </div>
      </section>
    </>
  );
}
